import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
  UserPlusIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { userService, User, CreateUserData, UpdateUserData } from '../services/userService';
import { ApiResponse } from '../types';
import { clientService } from '../services/clientService';
import LoadingSpinner from '../components/LoadingSpinner';
import { Pagination } from '../components/Pagination';
import AttachUsersModal from '../components/AttachUsersModal';
import { useAuth } from '../contexts/AuthContext';
import { useHeader } from '../contexts/HeaderContext';
import { isInternalDomain } from '../utils/authUtils';
import toast from 'react-hot-toast';

const Users: React.FC = () => {
  const { user: currentUser, masqueradeAsUser } = useAuth();
  const { setTitle } = useHeader();
  // Use custom claims permissions instead of role checks
  const hasAdminFull = currentUser?.permissions?.includes('admin:full');
  const hasUsersResetPassword = currentUser?.permissions?.includes('users:reset_password') || hasAdminFull;

  // Helper functions to check role-specific permissions
  const canWriteUserRole = (role: string): boolean => {
    if (hasAdminFull) return true;
    return currentUser?.permissions?.includes(`users:write:${role}`) || false;
  };

  const canDeleteUserRole = (role: string): boolean => {
    if (hasAdminFull) return true;
    return currentUser?.permissions?.includes(`users:delete:${role}`) || false;
  };
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [masqueradeTarget, setMasqueradeTarget] = useState<User | null>(null);
  const [searchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Reset page on filter/limit changes
  useEffect(() => {
    setPage(1);
  }, [roleFilter, clientFilter, limit]);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: '',
    clientId: (currentUser as any)?.clientId || undefined,
    clientIds: [],
  });
  const [showAddClientDropdown, setShowAddClientDropdown] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showWorkspaceDomainModal, setShowWorkspaceDomainModal] = useState(false);

  const queryClient = useQueryClient();

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', { search: searchTerm, role: roleFilter, clientId: clientFilter, page, limit }],
    queryFn: () =>
      userService.getUsers({
        search: searchTerm,
        role: roleFilter || undefined,
        clientId: clientFilter ? parseInt(clientFilter) : undefined,
        page,
        limit,
      }),
  });

  // Call-ahead prefetching in background
  useEffect(() => {
    const pagination = usersData?.pagination;
    if (pagination && page < pagination.totalPages) {
      const nextPage = page + 1;
      queryClient.prefetchQuery({
        queryKey: ['users', { search: searchTerm, role: roleFilter, clientId: clientFilter, page: nextPage, limit }],
        queryFn: () =>
          userService.getUsers({
            search: searchTerm,
            role: roleFilter || undefined,
            clientId: clientFilter ? parseInt(clientFilter) : undefined,
            page: nextPage,
            limit,
          }),
      });
    }
  }, [page, limit, searchTerm, roleFilter, clientFilter, usersData, queryClient]);

  // Sync query params with filters
  useEffect(() => {
    const clientIdParam = searchParams.get('client_id');
    if (clientIdParam) {
      setClientFilter(clientIdParam);
    }
  }, [searchParams]);

  // Debounce search input for user search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch clients for dropdown and filter
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 1000 }), // Get all clients
  });

  const users = usersData?.users || [];
  const pagination = usersData?.pagination;
  const clients = clientsData?.clients || [];
  const canAttachUsers = !!clientFilter && (
    hasAdminFull ||
    canWriteUserRole('ADVISOR') ||
    canWriteUserRole('CLIENT_ADMIN') ||
    canWriteUserRole('CLIENT_USER')
  );

  useEffect(() => {
    if (clientFilter) {
      const client = clients.find((c) => c.id.toString() === clientFilter);
      if (client) {
        setTitle(`${client.name} User Management`);
        return;
      }
    }
    setTitle('User Management');
  }, [clientFilter, clients, setTitle]);

  const workspaceLoginUrl = typeof window !== 'undefined' ? `${window.location.origin}/login?workspace_auth=true` : '/login?workspace_auth=true';

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users'], { refetchActive: true, refetchInactive: true });
      setShowCreateForm(false);
      resetForm();
      toast.success('User created successfully. They will be linked to Firebase when they sign up.');
    },
    onError: (error: any) => {
      const code = error?.response?.data?.error?.code;
      const url = error?.response?.data?.error?.workspaceLoginUrl;
      if (code === 'WORKSPACE_DOMAIN_USE_GOOGLE') {
        setShowWorkspaceDomainModal(true);
        return;
      }
      toast.error(error.message || 'Failed to create user');
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserData }) =>
      userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users'], { refetchActive: true, refetchInactive: true });
      setShowCreateForm(false);
      setEditingUser(null);
      resetForm();
      toast.success('User updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update user');
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: userService.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users'], { refetchActive: true, refetchInactive: true });
      toast.success('User deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete user');
    },
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: userService.deleteUserPermanent,
    onSuccess: () => {
      queryClient.invalidateQueries(['users'], { refetchActive: true, refetchInactive: true });
      toast.success('User permanently deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to permanently delete user');
    },
  });

  const restoreUserMutation = useMutation({
    mutationFn: userService.restoreUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users'], { refetchActive: true, refetchInactive: true });
      toast.success('User restored successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to restore user');
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: userService.resetPassword,
    onSuccess: (response: ApiResponse<{ newPassword: string }>) => {
      const newPassword = response.data?.newPassword;
      if (newPassword) {
        // Show password in a way that can be copied
        toast.success(
          () => (
            <div className="space-y-2">
              <p className="font-medium">Password reset successfully!</p>
              <div className="flex items-center space-x-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                  {newPassword}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(newPassword);
                    toast.success('Password copied to clipboard');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-600">
                Share this password with the user. They will need to sign in with it.
              </p>
            </div>
          ),
          { duration: 10000 }
        );
      } else {
        toast.success('Password reset successfully');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reset password');
    },
  });

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      role: '',
      clientId: (currentUser as any)?.clientId || undefined,
      clientIds: [],
    });
    setShowAddClientDropdown(false);
    setClientSearchTerm('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser && formData.email && isInternalDomain(formData.email)) {
      setShowWorkspaceDomainModal(true);
      return;
    }
    const submitData = {
      ...formData,
      clientIds: formData.clientIds && formData.clientIds.length > 0
        ? formData.clientIds
        : (formData.clientId ? [formData.clientId] : []),
    };
    if (editingUser) {
      updateUserMutation.mutate({ id: editingUser.id, data: submitData });
    } else {
      createUserMutation.mutate(submitData);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    // Collect all client IDs (primary client + clientAccess)
    const allClientIds: number[] = [];
    if (user.clientId) {
      allClientIds.push(user.clientId);
    }
    if (user.clientAccess) {
      user.clientAccess.forEach((access) => {
        if (!allClientIds.includes(access.clientId)) {
          allClientIds.push(access.clientId);
        }
      });
    }
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone || '',
      role: user.role,
      clientId: user.clientId || undefined,
      clientIds: allClientIds,
    });
    setShowCreateForm(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(id);
    }
  };

  const handleMasqueradeConfirm = async (targetUser: User) => {
    try {
      await masqueradeAsUser(targetUser.id);
      // No need to close modal or toast here as masqueradeAsUser handles redirection
    } catch (error) {
      console.error('Masquerade failed', error);
      setMasqueradeTarget(null);
    }
  };

  // Get available clients (filter out already assigned)
  const getAvailableClients = () => {
    const assignedClientIds = new Set(formData.clientIds || []);
    if (formData.clientId) {
      assignedClientIds.add(formData.clientId);
    }
    return clients.filter(
      (client) => !assignedClientIds.has(client.id) &&
        (clientSearchTerm === '' ||
          client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()))
    );
  };

  // Add a client to the user
  const handleAddClient = (clientId: number) => {
    const currentClientIds = formData.clientIds || [];
    if (!currentClientIds.includes(clientId)) {
      setFormData({
        ...formData,
        clientIds: [...currentClientIds, clientId],
        clientId: formData.clientId || clientId, // Set as primary if none exists
      });
    }
    setClientSearchTerm('');
    setShowAddClientDropdown(false);
  };

  // Remove a client from the user
  const handleRemoveClient = (clientId: number) => {
    const currentClientIds = formData.clientIds || [];
    const newClientIds = currentClientIds.filter((id) => id !== clientId);
    setFormData({
      ...formData,
      clientIds: newClientIds,
      clientId: formData.clientId === clientId
        ? (newClientIds.length > 0 ? newClientIds[0] : undefined)
        : formData.clientId,
    });
  };

  // Get client name by ID
  const getClientName = (clientId: number) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || `Client ${clientId}`;
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      SUPER_ADMIN: 'Super Admin',
      ADMIN: 'Admin',
      ADVISOR: 'Advisor',
      CLIENT_ADMIN: 'Client Admin',
      CLIENT_USER: 'Client User',
    };
    return roleMap[role] || role;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Removed early page-level isLoading spinner return to prevent unmounting search inputs.
  // Loading state is now handled downstream inside the users list container.

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            {(hasAdminFull || canWriteUserRole('CLIENT_USER') || canWriteUserRole('CLIENT_ADMIN') || canWriteUserRole('ADVISOR'))
              ? 'Manage system users and permissions'
              : 'View system users'}
          </p>
        </div>
        <div className="flex space-x-2">
          {canAttachUsers && (
            <button
              onClick={() => setShowAttachModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <UserPlusIcon className="w-4 h-4 mr-2" />
              Attach User
            </button>
          )}
          {(hasAdminFull || canWriteUserRole('CLIENT_USER') || canWriteUserRole('CLIENT_ADMIN') || canWriteUserRole('ADVISOR')) && (
            <button
              onClick={() => {
                setEditingUser(null);
                resetForm();
                setShowCreateForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New User
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        {hasAdminFull && (
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Roles</option>
            <option value="SUPER_ADMIN">Super Admin</option>
            <option value="ADMIN">Admin</option>
            <option value="ADVISOR">Advisor</option>
            <option value="CLIENT_ADMIN">Client Admin</option>
            <option value="CLIENT_USER">Client User</option>
          </select>
        )}
        {hasAdminFull && clients.length > 0 && (
          <select
            value={clientFilter}
            onChange={(e) => {
              setClientFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id.toString()}>
                {client.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Users List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            All Users {pagination && `(${pagination.total})`}
          </h3>
        </div>
        <div className="divide-y divide-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : users.length > 0 ? (
            users.map((user) => (
              <div key={user.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </h4>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <div className="mt-1 flex items-center space-x-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            user.status
                          )}`}
                        >
                          {user.status}
                        </span>
                        <span className="text-xs text-gray-400">
                          {getRoleDisplayName(user.role)}
                        </span>
                        {/* Show all clients (primary + clientAccess) */}
                        {(() => {
                          const allClients: string[] = [];
                          if (user.client) {
                            allClients.push(user.client.name);
                          }
                          if (user.clientAccess) {
                            user.clientAccess.forEach((access) => {
                              if (access.client && !allClients.includes(access.client.name)) {
                                allClients.push(access.client.name);
                              }
                            });
                          }
                          return allClients.length > 0 ? (
                            <span className="text-xs text-gray-400">
                              • {allClients.join(', ')}
                            </span>
                          ) : null;
                        })()}
                        {/* Show auth provider icons */}
                        {user.firebaseUser?.providers && user.firebaseUser.providers.length > 0 && (
                          <span className="flex items-center space-x-1">
                            {user.firebaseUser.providers.map((p) => {
                              const isGoogle = p.providerId === 'google.com';
                              const isPassword = p.providerId === 'password';
                              return (
                                <span key={p.providerId} className="inline-flex items-center" title={p.providerId}>
                                  {isGoogle ? (
                                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                                      <path
                                        fill="#4285F4"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                      />
                                      <path
                                        fill="#34A853"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                      />
                                      <path
                                        fill="#FBBC05"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                      />
                                      <path
                                        fill="#EA4335"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                      />
                                    </svg>
                                  ) : isPassword ? (
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                  ) : (
                                    <span className="text-xs">{p.providerId}</span>
                                  )}
                                </span>
                              );
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Show masquerade button for Super Admins on non-Super Admin users */}
                    {(currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'super_admin') &&
                      (user.role !== 'SUPER_ADMIN' && user.role !== 'super_admin') && (
                        <button
                          onClick={() => setMasqueradeTarget(user)}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title="Masquerade as user"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      )}
                    {/* Show edit button if user has write permission for this specific role */}
                    {canWriteUserRole(user.role) && (
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit user"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                    {hasUsersResetPassword &&
                      user.firebaseUid &&
                      user.firebaseUser?.providers?.some((p) => p.providerId === 'password') && (
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                `Reset password for ${user.firstName} ${user.lastName}? A new random password will be generated and displayed.`
                              )
                            ) {
                              resetPasswordMutation.mutate(user.id);
                            }
                          }}
                          className="text-purple-600 hover:text-purple-900 p-1"
                          title="Reset password (SuperAdmin only)"
                          disabled={resetPasswordMutation.isLoading}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                            />
                          </svg>
                        </button>
                      )}
                    {/* Show delete button if user has delete permission for this specific role */}
                    {canDeleteUserRole(user.role) && user.status !== 'deleted' && (
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete user (deactivate)"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                    {/* SuperAdmin only: restore deleted user */}
                    {(currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'super_admin') && user.status === 'deleted' && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Restore ${user.firstName} ${user.lastName}?`)) {
                            restoreUserMutation.mutate(user.id);
                          }
                        }}
                        className="text-green-600 hover:text-green-900 p-1"
                        title="Restore user"
                        disabled={restoreUserMutation.isLoading}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    )}
                    {/* SuperAdmin only: permanently delete user */}
                    {(currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'super_admin') &&
                      user.role !== 'SUPER_ADMIN' && user.role !== 'super_admin' && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Permanently delete ${user.firstName} ${user.lastName}? This cannot be undone and will remove the user from Firebase and the database.`)) {
                              permanentDeleteMutation.mutate(user.id);
                            }
                          }}
                          className="text-red-700 hover:text-red-900 p-1"
                          title="Permanently delete user (SuperAdmin only)"
                          disabled={permanentDeleteMutation.isLoading}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <h3 className="text-sm font-medium text-gray-900">No users found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || roleFilter
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by creating a new user.'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && (
          <Pagination
            page={page}
            limit={limit}
            total={pagination.total}
            totalPages={pagination.totalPages}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        )}
      </div>

      {/* Masquerade Confirmation Modal */}
      {masqueradeTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setMasqueradeTarget(null)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <EyeIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Masquerade as {masqueradeTarget.firstName} {masqueradeTarget.lastName}?
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You are about to sign in as this user to explore the application from their perspective.
                      Clicking "Yes" will simulate a login as {masqueradeTarget.firstName}.
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      To return to your Super Admin session, click the "Logout" button (which will show as "Exit Masquerade").
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => handleMasqueradeConfirm(masqueradeTarget)}
                >
                  Yes, Masquerade
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() => setMasqueradeTarget(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workspace domain: use Google sign-in modal */}
      {showWorkspaceDomainModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowWorkspaceDomainModal(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Use Google Workspace sign-in</h3>
              <p className="text-sm text-gray-600 mb-4">
                Users with this email domain (e.g. @aprco.com) must sign in with Google. Do not create a password account.
              </p>
              <p className="text-sm text-gray-700 mb-2">Ask the new user to open this link to sign in:</p>
              <div className="flex items-center gap-2 mb-4">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm break-all">{workspaceLoginUrl}</code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(workspaceLoginUrl);
                    toast.success('URL copied to clipboard');
                  }}
                  className="flex-shrink-0 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Copy
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                After their first login, adjust their roles and permissions in User Management as needed.
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowWorkspaceDomainModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attach Users Modal */}
      {showAttachModal && clientFilter && (
        <AttachUsersModal
          clientId={parseInt(clientFilter)}
          clientName={clients.find(c => c.id === parseInt(clientFilter))?.name || 'Client'}
          onClose={() => setShowAttachModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setShowAttachModal(false);
          }}
        />
      )}

      {/* Create/Edit User Modal */}
      {showCreateForm && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={(e) => {
            // Close dropdown if clicking outside the modal content
            if (e.target === e.currentTarget) {
              setShowAddClientDropdown(false);
            }
          }}
        >
          <div
            className="relative top-20 mx-auto p-5 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={!!editingUser}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {editingUser
                      ? 'Email cannot be changed'
                      : ''}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {hasAdminFull && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({ ...formData, role: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select role</option>
                      {hasAdminFull && <option value="SUPER_ADMIN">Super Admin</option>}
                      <option value="ADMIN">Admin</option>
                      <option value="ADVISOR">Advisor</option>
                      <option value="CLIENT_ADMIN">Client Admin</option>
                      <option value="CLIENT_USER">Client User</option>
                    </select>
                  </div>
                )}
                {!hasAdminFull && (canWriteUserRole('CLIENT_USER') || canWriteUserRole('CLIENT_ADMIN')) && clients.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Client *
                    </label>
                    <select
                      value={formData.clientId || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          clientId: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select client</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {hasAdminFull && clients.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Clients
                    </label>

                    {/* Display assigned clients as removable labels */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.clientId && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {getClientName(formData.clientId)}
                          <button
                            type="button"
                            onClick={() => handleRemoveClient(formData.clientId!)}
                            className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 focus:outline-none"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {formData.clientIds?.map((clientId) => {
                        if (clientId === formData.clientId) return null; // Skip primary client
                        return (
                          <span
                            key={clientId}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                          >
                            {getClientName(clientId)}
                            <button
                              type="button"
                              onClick={() => handleRemoveClient(clientId)}
                              className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 focus:outline-none"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>

                    {/* Add Client Button and Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddClientDropdown(!showAddClientDropdown);
                          setClientSearchTerm('');
                        }}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <PlusIcon className="w-4 h-4 mr-1" />
                        Add Client
                      </button>

                      {showAddClientDropdown && (
                        <div className="absolute z-10 mt-1 w-64 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                          <div className="sticky top-0 bg-white p-2 border-b">
                            <input
                              type="text"
                              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="Search clients..."
                              value={clientSearchTerm}
                              onChange={(e) => setClientSearchTerm(e.target.value)}
                              autoFocus
                            />
                          </div>
                          {getAvailableClients().length > 0 ? (
                            getAvailableClients().map((client) => (
                              <div
                                key={client.id}
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
                                onClick={() => handleAddClient(client.id)}
                              >
                                <span className="block truncate font-normal">
                                  {client.name}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
                              No clients found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createUserMutation.isLoading || updateUserMutation.isLoading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createUserMutation.isLoading || updateUserMutation.isLoading ? (
                      <LoadingSpinner size="sm" color="white" />
                    ) : editingUser ? (
                      'Update User'
                    ) : (
                      'Create User'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
